import React from 'react';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  variant = 'danger',
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const getVariantColor = () => {
    switch (variant) {
      case 'danger':
        return {
          gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          shadow: '0 4px 14px rgba(239, 68, 68, 0.4)',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          ),
        };
      case 'warning':
        return {
          gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          shadow: '0 4px 14px rgba(245, 158, 11, 0.4)',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          ),
        };
      case 'info':
        return {
          gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          shadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
          icon: (
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          ),
        };
      default:
        return {
          gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          shadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
          icon: null,
        };
    }
  };

  const variantStyles = getVariantColor();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
        animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'var(--card)',
          borderRadius: 16,
          padding: 32,
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.3), 0 8px 16px rgba(0, 0, 0, 0.2)',
          animation: 'modalBounce 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          border: '1px solid var(--border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 24,
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'var(--muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
          }}>
            {variantStyles.icon}
          </div>
          <h3
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--card-foreground)',
              marginBottom: 12,
              textAlign: 'center',
              letterSpacing: '-0.5px',
            }}
          >
            {title}
          </h3>
          <p
            style={{
              color: 'var(--muted-foreground)',
              fontSize: 15,
              lineHeight: 1.6,
              textAlign: 'center',
              maxWidth: 380,
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              background: 'var(--muted)',
              color: 'var(--foreground)',
              border: '2px solid var(--border)',
              padding: '12px 28px',
              borderRadius: 10,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 15,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              minWidth: 120,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--card)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              background: variantStyles.gradient,
              color: 'white',
              border: 'none',
              padding: '12px 28px',
              borderRadius: 10,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 15,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: variantStyles.shadow,
              minWidth: 120,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = variantStyles.shadow;
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes modalBounce {
            0% {
              transform: scale(0.7) translateY(-20px);
              opacity: 0;
            }
            50% {
              transform: scale(1.05);
            }
            100% {
              transform: scale(1) translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
}
